"use client"
import { useState } from "react"
import { useRouter } from "next/navigation"
import { StarRating } from "@/components/StarRating"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"

const GRANULAR_CATEGORIES = [
  { key: "housingRating", label: "Housing Quality" },
  { key: "studioRating", label: "Studio Quality" },
  { key: "communityRating", label: "Community / Networking" },
  { key: "foodRating", label: "Food" },
  { key: "staffRating", label: "Staff Support" },
  { key: "valueRating", label: "Value for Money" },
  { key: "adminCommsRating", label: "Communication / Organization" },
]

const STIPEND_OPTIONS = [
  { value: "", label: "Prefer not to say" },
  { value: "na", label: "No stipend was offered" },
  { value: "yes", label: "Yes, paid as promised" },
  { value: "partial", label: "Partially" },
  { value: "no", label: "No" },
]

export type ReviewFormInitial = {
  id: string
  rating: number
  title: string
  body: string
  pros: string | null
  cons: string | null
  year: number | null
  isAnonymous: boolean
  pseudonym: string | null
  housingRating: number | null
  studioRating: number | null
  communityRating: number | null
  foodRating: number | null
  staffRating: number | null
  valueRating: number | null
  adminCommsRating: number | null
  stipendAsPromised: string | null
  totalCostUsd: number | null
  wouldRecommend: boolean | null
  attendedDurationWks: number | null
}

const GRANULAR_KEYS = GRANULAR_CATEGORIES.map((c) => c.key) as (keyof ReviewFormInitial)[]

export function ReviewForm({
  residencyId,
  initial,
  onCancel,
}: {
  residencyId: string
  /** When set, the form edits this existing review (PATCH) instead of creating one. */
  initial?: ReviewFormInitial
  /** Shown as a Cancel button in edit mode. */
  onCancel?: () => void
}) {
  const router = useRouter()
  const isEditing = !!initial
  const [rating, setRating] = useState(initial?.rating ?? 0)
  const [title, setTitle] = useState(initial?.title ?? "")
  const [body, setBody] = useState(initial?.body ?? "")
  const [pros, setPros] = useState(initial?.pros ?? "")
  const [cons, setCons] = useState(initial?.cons ?? "")
  const [year, setYear] = useState(initial?.year ? String(initial.year) : "")
  const [isAnonymous, setIsAnonymous] = useState(initial?.isAnonymous ?? false)
  const [pseudonym, setPseudonym] = useState(initial?.pseudonym ?? "")
  const [granularRatings, setGranularRatings] = useState<Record<string, number>>(() => {
    if (!initial) return {}
    const seeded: Record<string, number> = {}
    for (const k of GRANULAR_KEYS) {
      const v = initial[k]
      if (typeof v === "number" && v > 0) seeded[k] = v
    }
    return seeded
  })
  const [wouldRecommend, setWouldRecommend] = useState<boolean | null>(initial?.wouldRecommend ?? null)
  const [stipendAsPromised, setStipendAsPromised] = useState(initial?.stipendAsPromised ?? "")
  const [totalCostUsd, setTotalCostUsd] = useState(
    initial?.totalCostUsd != null ? String(initial.totalCostUsd) : ""
  )
  const [attendedDurationWks, setAttendedDurationWks] = useState(
    initial?.attendedDurationWks != null ? String(initial.attendedDurationWks) : ""
  )
  const [showGranular, setShowGranular] = useState(
    initial
      ? GRANULAR_KEYS.some((k) => typeof initial[k] === "number" && (initial[k] as number) > 0) ||
        initial.wouldRecommend != null || !!initial.stipendAsPromised ||
        initial.totalCostUsd != null || initial.attendedDurationWks != null
      : false
  )
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState("")

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (rating === 0) { setError("Please select a star rating."); return }
    if (!title.trim()) { setError("Please add a review title."); return }
    if (body.trim().length < 30) { setError("Review must be at least 30 characters."); return }

    setSubmitting(true)
    setError("")
    try {
      // Editing PATCHes the specific review; a new review POSTs to the collection.
      // Clearing a granular rating in edit mode must send null to unset it, so we
      // always include every granular key when editing.
      const granularPayload = isEditing
        ? Object.fromEntries(GRANULAR_KEYS.map((k) => [k, granularRatings[k] || null]))
        : Object.fromEntries(Object.entries(granularRatings).filter(([, v]) => v > 0))

      const res = await fetch(isEditing ? `/api/reviews/${initial!.id}` : "/api/reviews", {
        method: isEditing ? "PATCH" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...(isEditing ? {} : { residencyId }),
          rating,
          title,
          body,
          pros,
          cons,
          year: year ? parseInt(year) : null,
          isAnonymous,
          pseudonym: isAnonymous ? pseudonym || null : null,
          ...granularPayload,
          wouldRecommend,
          stipendAsPromised: stipendAsPromised || null,
          totalCostUsd: totalCostUsd !== "" ? parseFloat(totalCostUsd) : null,
          attendedDurationWks: attendedDurationWks !== "" ? parseFloat(attendedDurationWks) : null,
        }),
      })
      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error ?? "Failed to submit review")
      }
      onCancel?.()
      router.refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="border border-[var(--border)] rounded-xl p-6 space-y-4">
      {/* Star picker */}
      <div>
        <label className="text-sm font-medium block mb-2">Overall Rating *</label>
        <StarRating rating={rating} interactive onChange={setRating} size={28} />
        {rating > 0 && (
          <span className="text-xs text-[var(--muted-foreground)] mt-1 block">
            {["", "Poor", "Fair", "Good", "Very Good", "Excellent"][rating]}
          </span>
        )}
      </div>

      {/* Granular ratings (expandable) */}
      <div>
        <button
          type="button"
          onClick={() => setShowGranular(!showGranular)}
          className="text-[13px] text-[var(--muted-foreground)] hover:text-[var(--foreground)] transition-colors underline-offset-2 hover:underline"
        >
          {showGranular ? "Hide detailed ratings" : "Rate specific aspects (optional)"}
        </button>
        {showGranular && (
          <div className="mt-3 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {GRANULAR_CATEGORIES.map((cat) => (
                <div key={cat.key} className="flex items-center justify-between gap-2 bg-[var(--secondary)] rounded-lg px-3 py-2">
                  <span className="text-[13px] text-[var(--foreground)]">{cat.label}</span>
                  <StarRating
                    rating={granularRatings[cat.key] ?? 0}
                    interactive
                    onChange={(v) => setGranularRatings((prev) => ({ ...prev, [cat.key]: v }))}
                    size={16}
                  />
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="bg-[var(--secondary)] rounded-lg px-3 py-2">
                <span className="text-[13px] text-[var(--foreground)] block mb-1.5">Would you recommend it?</span>
                <div className="flex gap-1.5">
                  {[
                    { value: true, label: "Yes" },
                    { value: false, label: "No" },
                  ].map((opt) => (
                    <button
                      key={String(opt.value)}
                      type="button"
                      onClick={() => setWouldRecommend(wouldRecommend === opt.value ? null : opt.value)}
                      aria-pressed={wouldRecommend === opt.value}
                      className={`px-3 py-1 rounded-md border text-[12px] transition-colors ${
                        wouldRecommend === opt.value
                          ? "bg-[var(--foreground)] text-white border-[var(--foreground)]"
                          : "bg-white text-[var(--muted-foreground)] border-[var(--border)] hover:border-[var(--foreground)]"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="bg-[var(--secondary)] rounded-lg px-3 py-2">
                <span className="text-[13px] text-[var(--foreground)] block mb-1.5">Stipend paid as promised?</span>
                <select
                  value={stipendAsPromised}
                  onChange={(e) => setStipendAsPromised(e.target.value)}
                  className="w-full rounded-md border border-[var(--input)] bg-white px-2 py-1 text-[12px] text-[var(--foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--ring)]"
                >
                  {STIPEND_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="bg-[var(--secondary)] rounded-lg px-3 py-2">
                <span className="text-[13px] text-[var(--foreground)] block mb-1.5">
                  What did it actually cost you? <span className="text-[var(--muted-foreground)]">(USD, all-in)</span>
                </span>
                <Input
                  type="number"
                  min={0}
                  step="1"
                  value={totalCostUsd}
                  onChange={(e) => setTotalCostUsd(e.target.value)}
                  placeholder="e.g. 2400 — travel, fees, lost income"
                  className="bg-white"
                />
              </div>
              <div className="bg-[var(--secondary)] rounded-lg px-3 py-2">
                <span className="text-[13px] text-[var(--foreground)] block mb-1.5">How long were you there? <span className="text-[var(--muted-foreground)]">(weeks)</span></span>
                <Input
                  type="number"
                  min={0.5}
                  step="0.5"
                  value={attendedDurationWks}
                  onChange={(e) => setAttendedDurationWks(e.target.value)}
                  placeholder="e.g. 4"
                  className="bg-white"
                />
              </div>
            </div>
            <p className="text-[11px] text-[var(--muted-foreground)]">
              All optional. Cost and stipend answers are only ever shown in aggregate — never as an individual data point.
            </p>
          </div>
        )}
      </div>

      <div>
        <label className="text-sm font-medium block mb-1">Review Title *</label>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Summarize your experience..."
          maxLength={120}
        />
      </div>

      <div>
        <label className="text-sm font-medium block mb-1">Your Review *</label>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Share your honest experience attending this residency..."
          rows={5}
          className="w-full rounded-[var(--radius)] border border-[var(--input)] bg-white px-3 py-2 text-sm text-[var(--foreground)] placeholder:text-[var(--muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--ring)] focus:border-transparent transition-all resize-y"
        />
        <div className="text-xs text-[var(--muted-foreground)] text-right">{body.length} chars (min 30)</div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium block mb-1">Pros (optional)</label>
          <textarea
            value={pros}
            onChange={(e) => setPros(e.target.value)}
            placeholder="What did you love about it?"
            rows={3}
            className="w-full rounded-[var(--radius)] border border-[var(--input)] bg-white px-3 py-2 text-sm placeholder:text-[var(--muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--ring)] resize-y"
          />
        </div>
        <div>
          <label className="text-sm font-medium block mb-1">Cons (optional)</label>
          <textarea
            value={cons}
            onChange={(e) => setCons(e.target.value)}
            placeholder="What could be improved?"
            rows={3}
            className="w-full rounded-[var(--radius)] border border-[var(--input)] bg-white px-3 py-2 text-sm placeholder:text-[var(--muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--ring)] resize-y"
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-4 items-end">
        <div className="w-32">
          <label className="text-sm font-medium block mb-1">Year Attended</label>
          <Input
            type="number"
            value={year}
            onChange={(e) => setYear(e.target.value)}
            placeholder="e.g. 2024"
            min={1990}
            max={new Date().getFullYear()}
          />
        </div>

        {/* Anonymous toggle */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="anonymous"
            checked={isAnonymous}
            onChange={(e) => setIsAnonymous(e.target.checked)}
            className="rounded border-[var(--border)]"
          />
          <label htmlFor="anonymous" className="text-sm text-[var(--muted-foreground)]">
            Post anonymously
          </label>
        </div>
      </div>

      {/* Anonymous pseudonym */}
      {isAnonymous && (
        <div>
          <label className="text-sm font-medium block mb-1">Display name (optional)</label>
          <Input
            value={pseudonym}
            onChange={(e) => setPseudonym(e.target.value)}
            placeholder='e.g. "Painter from Brooklyn"'
            maxLength={60}
          />
          <p className="text-[11px] text-[var(--muted-foreground)] mt-1">
            Your identity will be hidden. You can optionally use a pseudonym.
          </p>
        </div>
      )}

      <div className="bg-[var(--secondary)] rounded-lg px-4 py-3 text-xs text-[var(--muted-foreground)] space-y-1">
        <p className="font-medium text-[var(--foreground)]">Before you submit</p>
        <p>Only review residencies you&apos;ve actually attended. Be honest and specific — negative reviews are welcome as long as they&apos;re factual. Focus on the program, not individuals by name.</p>
        <p>
          Full details in our{" "}
          <a href="/guidelines#reviews" className="text-[var(--primary)] hover:underline">Community Guidelines</a>.
        </p>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="flex items-center gap-2">
        <Button type="submit" disabled={submitting}>
          {submitting
            ? (isEditing ? "Saving..." : "Submitting...")
            : (isEditing ? "Save changes" : "Submit Review")}
        </Button>
        {isEditing && onCancel && (
          <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
        )}
      </div>
    </form>
  )
}
